/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getSelectedVariantAction, SelectedVariantInput } from '../themes/dobbies/actions/get-selected-variant.override.action';
import { IProductInventoryInformation, mapProductInventoryInformation } from '../themes/dobbies/utilities';
import { getSelectedProductIdFromActionInput } from '../themes/dobbies/actions/utilities/rating-utils';

/**
 * GetBuyboxCncAvailabilities Input Action
 */
export class GetBuyboxCncAvailabilitiesInput implements Msdyn365.IActionInput {
    public productId: number;
    // TODO: Construct the input needed to run the action
    constructor(productId: number) {
        this.productId = productId;
    }

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => `BUYBOX_CNC_AVAILABILITIES_${this.productId}`;
    public getCacheObjectType = () => 'BUYBOX_CNC_AVAILABILITIES';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

// TODO: Create a data model here or import one to capture the response of the action
export interface IGetBuyboxCncAvailabilitiesData {
    availabilities: IProductInventoryInformation[];
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    const productId = getSelectedProductIdFromActionInput(args);
    if (productId) {
        return new GetBuyboxCncAvailabilitiesInput(+productId);
    } else {
        throw new Error('Unable to create GetBuyboxCncAvailabilitiesInput, no productId found');
    }
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function action(input: GetBuyboxCncAvailabilitiesInput, ctx: Msdyn365.IActionContext): Promise<IGetBuyboxCncAvailabilitiesData> {
    const selectedVariantInput = new SelectedVariantInput(input.productId, ctx.requestContext.apiSettings.channelId);
    try {
        const product = await getSelectedVariantAction(selectedVariantInput, ctx);
        const productAvailabiliteCNC = await getEstimatedAvailabilityAsync(
            { callerContext: ctx },
            { ProductIds: [product?.RecordId || 0], DefaultWarehouseOnly: false, FilterByChannelFulfillmentGroup: true }
        );
        const mappedInventory = mapProductInventoryInformation(ctx, productAvailabiliteCNC.ProductWarehouseInventoryAvailabilities || []);
        return { availabilities: mappedInventory };
    } catch (error) {
        if (error instanceof Error) {
            ctx.telemetry.error(`Error in GetBuyboxCncAvailabilities action: ${error.message}`);
        }
        return { availabilities: [] };
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetBuyboxCncAvailabilitiesData>>action,
    // TODO: Give your data action a unique id
    id: 'GetBuyboxCncAvailabilities',
    input: createInput
    // TODO: Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});
